import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Check } from "lucide-react";
import { suggestHeadlines } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmojiCarousel } from "./EmojiCarousel";

export function HeadlineField({ horseId, value, onChange, locked, beforeGenerate, emoji, onEmoji, emojiLocked, emojiRequired, emojiError, headlineError }) {
  const [ideas, setIdeas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      await beforeGenerate?.();
      setIdeas(await suggestHeadlines(horseId));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate headlines"); }
    finally { setBusy(false); }
  };
  const pick = (h) => {
    if ((value || "").trim() && value.trim() !== h) setConfirm(h);
    else onChange(h);
  };

  return (
    <div data-testid="headline-field">
      <label htmlFor="field-horse-ad_catchy_header" className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">Catchy Ad Headline <span data-testid="headline-required" className="text-red-600">*</span></label>
      <Input id="field-horse-ad_catchy_header" data-testid="field-horse-ad_catchy_header" value={value || ""} readOnly={locked}
        onChange={(e) => onChange(e.target.value)} placeholder="Write your own headline or generate ideas"
        className={`focus-visible:ring-amber-800 ${headlineError ? "border-red-400" : "border-stone-200"}`} aria-invalid={headlineError || undefined} />
      {headlineError && <p data-testid="headline-error" className="mt-1 text-xs text-red-600">Please enter a catchy ad headline</p>}
      {onEmoji && <EmojiCarousel value={emoji} onChange={onEmoji} disabled={emojiLocked} required={emojiRequired} error={emojiError} />}
      {!locked && (
        <Button data-testid="generate-headlines-button" type="button" variant="outline" onClick={generate} disabled={busy}
          className="mt-2 rounded-full border-amber-800 text-amber-800 hover:bg-amber-50 h-9 text-sm">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}Generate AI Headlines
        </Button>
      )}
      {confirm && (
        <div data-testid="headline-replace-confirm" className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          Replace your headline with “{confirm}”?
          <div className="mt-2 flex gap-2">
            <Button data-testid="headline-replace-yes" size="sm" onClick={() => { onChange(confirm); setConfirm(null); }} className="rounded-full bg-amber-800 hover:bg-amber-900 text-white h-8">Replace</Button>
            <Button data-testid="headline-replace-no" size="sm" variant="outline" onClick={() => setConfirm(null)} className="rounded-full h-8">Keep mine</Button>
          </div>
        </div>
      )}
      {ideas.length > 0 && !locked && (
        <ul data-testid="headline-suggestions" className="mt-2 space-y-1.5">
          {ideas.map((h, i) => (
            <li key={h}>
              <button type="button" data-testid={`headline-suggestion-${i}`} onClick={() => pick(h)}
                className={`w-full text-left text-sm rounded-lg border px-3 py-2 transition-colors duration-150 flex items-center gap-2 ${value === h ? "border-amber-800 bg-amber-50 text-amber-900" : "border-stone-200 bg-white hover:border-amber-400"}`}>
                {value === h && <Check className="h-4 w-4 shrink-0" />}<span>{h}</span>
              </button>
            </li>
          ))}
          <li className="text-[11px] text-stone-400">Tap a suggestion to use it — you can edit it afterwards. Suggestions only use information you've provided.</li>
        </ul>
      )}
    </div>
  );
}
